import React, { useRef } from 'react'
import { themeColor } from '@edulastic/colors'
import { useJoyrideFocusTrap } from './useJoyrideFocusTrap'

const tooltipShellStyle = {
  backgroundColor: '#fff',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  color: '#2f3542',
  maxWidth: 360,
  padding: 16,
  textAlign: 'left',
}

const headerRowStyle = {
  alignItems: 'flex-start',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 12,
}

const closeButtonStyle = {
  background: 'transparent',
  border: 0,
  color: '#5b6573',
  cursor: 'pointer',
  fontSize: 24,
  lineHeight: 1,
  margin: 0,
  minHeight: 32,
  minWidth: 32,
  padding: 4,
}

const primaryButtonStyle = {
  background: 'transparent',
  border: 0,
  color: themeColor,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 700,
  marginTop: 16,
  minHeight: 32,
  padding: '8px 0',
}

const focusVisibleProps = {
  onFocus: (event) => {
    event.currentTarget.style.outline = `2px solid ${themeColor}`
    event.currentTarget.style.outlineOffset = '2px'
  },
  onBlur: (event) => {
    event.currentTarget.style.outline = ''
    event.currentTarget.style.outlineOffset = ''
  },
}

const stopDismissClickThrough = (event) => {
  event.preventDefault()
  event.stopPropagation()
}

const EducationJoyrideTooltip = ({
  closeProps,
  primaryProps,
  step,
  tooltipProps,
  variant,
  moderateIcon: ModerateIcon,
  titleId,
  descId,
  onDismissInteraction,
}) => {
  const tooltipRef = useRef(null)
  const isModerate = variant === 'moderate'

  useJoyrideFocusTrap(tooltipRef, true)

  const { ref: joyrideTooltipRef, ...restTooltipProps } = tooltipProps

  const setTooltipRef = (node) => {
    tooltipRef.current = node
    joyrideTooltipRef?.(node)
  }

  const handleDismissPointerDown = () => {
    onDismissInteraction?.()
  }

  const handleCloseClick = (event) => {
    stopDismissClickThrough(event)
    closeProps?.onClick?.(event)
  }

  const handlePrimaryClick = (event) => {
    stopDismissClickThrough(event)
    primaryProps?.onClick?.(event)
  }

  return (
    // Dialog intercepts pointer events so clicks can't fall through to the
    // (blurred) page behind an aggressive joyride.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      {...restTooltipProps}
      ref={setTooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      style={tooltipShellStyle}
      onMouseDown={stopDismissClickThrough}
      onClick={stopDismissClickThrough}
    >
      {isModerate && ModerateIcon ? (
        <div style={headerRowStyle}>
          <div aria-hidden="true">
            <ModerateIcon />
          </div>
          <button
            type="button"
            {...closeProps}
            {...focusVisibleProps}
            aria-label={closeProps['aria-label'] || 'Close'}
            style={closeButtonStyle}
            onMouseDown={(event) => {
              stopDismissClickThrough(event)
              handleDismissPointerDown()
            }}
            onClick={handleCloseClick}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ) : null}

      <div>{step.content}</div>

      <div>
        <button
          type="button"
          {...primaryProps}
          {...focusVisibleProps}
          style={primaryButtonStyle}
          onMouseDown={(event) => {
            stopDismissClickThrough(event)
            handleDismissPointerDown()
          }}
          onClick={handlePrimaryClick}
        >
          {primaryProps.title}
        </button>
      </div>
    </div>
  )
}

export default EducationJoyrideTooltip
